/**
 * firmanteDeFuera.js — apuntar a firmar a alguien que no está en el directorio.
 *
 * ── Por qué hacía falta ──────────────────────────────────────────────────
 *
 * A una reunión va gente de fuera: un cliente, un notario, el consejero que no
 * tiene cuenta en la empresa. Firman el acta igual que los demás, y hasta ahora
 * la lista de firmantes solo ofrecía el directorio de la organización, así que
 * no había manera de apuntarlos. La única salida era bajarse el PDF y mandarlo
 * a mano, que es justo lo que esta pantalla vino a quitar.
 *
 * El servidor nunca lo impidió: guarda `{nombre, correo}` y solo exige que el
 * correo tenga forma de correo. La puerta estaba cerrada únicamente en la
 * pantalla.
 *
 * ── Por qué el correo es obligatorio aquí y en Planner no ────────────────
 *
 * Porque son dos cosas distintas con la misma pinta. A un responsable externo
 * de Planner se le apunta el NOMBRE: queda escrito en el acta y no se le crea
 * tarea, y con eso basta. A un firmante hay que MANDARLE algo: el correo con su
 * enlace es la petición de firma. Sin dirección no hay nada que mandar, y
 * admitirlo dejaría en la lista a alguien que no va a firmar nunca y que
 * además bloquea la ronda si va por turnos.
 *
 * Por eso aquí un nombre suelto no se acepta: se explica qué falta.
 */

// La misma regla que usa el servidor al guardar la ronda (`ES_CORREO`, en
// SignatureRoundRepository). Si no coincidieran, la pantalla aceptaría
// direcciones que el servidor descarta en silencio y el firmante se quedaría
// fuera de la ronda sin que nadie se entere.
const ES_CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** De «juan.perez.lopez» sale «Juan Perez Lopez». */
function nombreDesdeCorreo(correo) {
  const antes = String(correo).split('@')[0];
  return antes
    .split(/[._-]+/)
    .filter(Boolean)
    .map(t => t.charAt(0).toUpperCase() + t.slice(1))
    .join(' ');
}

/**
 * Lo que se ha escrito en la caja de búsqueda, convertido en firmante.
 *
 * Se admiten las tres formas en que alguien escribe a una persona, porque las
 * tres salen de copiar y pegar de un correo:
 *
 *     ana@fuera.com
 *     Ana López <ana@fuera.com>
 *     Ana López ana@fuera.com
 *
 * Devuelve:
 *   · `{ nombre, correo, aMano: true }` si hay una dirección utilizable
 *   · `{ aviso }` si se escribió algo que no lleva correo — un nombre a secas,
 *     casi siempre. No se calla: quien escribe «Luis Soto» y no ve nada creería
 *     que la lista está rota, no que le falta el correo.
 *   · `null` si no hay nada escrito
 *
 * Cuando no se da nombre se deduce del correo. Un nombre importa: aparece en el
 * acta, en el aviso al autor y en la cabecera del correo de firma, y poner ahí
 * «ana@fuera.com» convierte un documento en un volcado de base de datos.
 */
export function interpretarFirmante(texto) {
  const limpio = String(texto || '').trim();
  if (!limpio) return null;

  // Entre ángulos primero: es como lo pega un cliente de correo, y ahí el
  // nombre viene regalado.
  const conAngulos = limpio.match(/^(.*?)<\s*([^<>\s]+)\s*>$/);
  if (conAngulos) {
    const correo = conAngulos[2].trim().toLowerCase();
    if (!ES_CORREO.test(correo)) {
      return { aviso: `«${conAngulos[2].trim()}» no parece una dirección de correo.` };
    }
    const nombre = conAngulos[1].trim().replace(/^["']|["']$/g, '');
    return { id: '', nombre: nombre || nombreDesdeCorreo(correo), correo, puesto: '', aMano: true };
  }

  // Si no, la primera cosa que parezca un correo; lo demás es el nombre.
  const trozos = limpio.split(/\s+/);
  const dondeElCorreo = trozos.findIndex(t => ES_CORREO.test(t.toLowerCase()));

  if (dondeElCorreo < 0) {
    // Lleva arroba pero no pasa: es un correo mal escrito, no un nombre. Se
    // distingue porque el consejo es distinto —revisar lo escrito, no añadir
    // una dirección que ya se intentó poner.
    if (limpio.includes('@')) {
      return { aviso: `«${limpio}» no parece una dirección de correo.` };
    }
    return {
      aviso: `Para que «${limpio}» pueda firmar hace falta su correo: `
        + 'escríbelo detrás del nombre.'
    };
  }

  const correo = trozos[dondeElCorreo].toLowerCase();
  const nombre = trozos.filter((_, i) => i !== dondeElCorreo).join(' ').trim();
  return { id: '', nombre: nombre || nombreDesdeCorreo(correo), correo, puesto: '', aMano: true };
}

/** Si esta persona vino escrita a mano y no del directorio. */
export const esDeFuera = (quien) => Boolean(quien && (quien.aMano || !quien.id));
